import React, { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import {
  getUserInfo,
  hasRole,
  hasAnyRole,
  hasAllRoles,
  getToken,
  isTokenExpired,
  updateToken,
  login as doLogin,
  logout as doLogout,
  register as doRegister,
} from '../services/keycloak';
import type { User, AuthContextType } from '../types/auth';
import { AuthContext } from './AuthContextDefinition';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const refreshState = useCallback(() => {
    const currentToken = getToken();
    const authenticated = !!currentToken && !isTokenExpired();
    setIsAuthenticated(authenticated);
    setToken(authenticated ? (currentToken ?? null) : null);
    setUser(authenticated ? getUserInfo() : null);
    return authenticated;
  }, []);

  useEffect(() => {
    refreshState();
    setIsLoading(false);

    // Refresh the access token every minute if we have a session
    const interval = setInterval(async () => {
      if (!isTokenExpired()) return;
      try {
        const refreshed = await updateToken(30);
        if (refreshed) refreshState();
      } catch {
        // Refresh token expired — clear state but don't force-redirect
        setIsAuthenticated(false);
        setToken(null);
        setUser(null);
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [refreshState]);

  const login = () => doLogin();
  const logout = () => doLogout();
  const register = () => doRegister();

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    register,
    hasRole: role => hasRole(role),
    hasAnyRole: roles => hasAnyRole(roles),
    hasAllRoles: roles => hasAllRoles(roles),
    token: token ?? null,
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: 'var(--bg-primary)',
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            border: '3px solid transparent',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
